import { Directive } from '@angular/core';
import { Validators } from '@angular/forms';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import {
  createLoanPaliersArray,
  withLoanFormValidity,
} from '@app/services/loan/loan-palier.form';
import {
  applyPaliersToLoanPayload,
  hasLoanPaliers,
} from '@app/services/loan/loan-palier.utils';
import { isLoanDiffereSectionActive } from '@app/services/loan/loan-palier.validators';
import * as _ from 'lodash';

@Directive()
export abstract class EaLoanFormBuilder extends PrFormAbstract {
  generate(entity: any, options: any = {}) {
    const form = this.formBuilder.group(
      {
        id: [entity ? entity.id : null],
        delegationAssurance: [
          entity && !_.isNil(entity.delegationAssurance)
            ? entity.delegationAssurance
            : false,
        ],
        label: [entity ? entity.label : null, Validators.required],
        category: [entity ? entity.category : null, Validators.required],
        accountId: [entity ? entity.accountId : null],
        providerId: [entity ? entity.providerId : null],
        agenceBancaireId: [entity ? entity.agenceBancaireId : null],
        investmentId: [
          entity && entity.investmentIds ? entity.investmentIds[0] : null,
        ],
        // Détenteurs avec part de détention (`share`, fraction 0..1). Le champ
        // `ownerIds` est déprécié : on utilise désormais `owners`.
        owners: [
          this.buildLoanInitialOwners(entity, options),
          Validators.required,
        ],
        //providerId: [entity ? entity.providerId : null],

        mode: [
          this.entity.differe != null || hasLoanPaliers(this.entity)
            ? false
            : true,
          [
            this.prFormService.RefreshEveryValidators([
              'type',
              'insuranceQuotite',
              'durationDiffereInMonth',
            ]),
          ],
        ],

        type: [
          this.entity.type != null ? this.entity.type : 'FIX',
          [
            this.prFormService.AddValidatorsBasedOnCondition(
              this.prFormService
                .isTargetControlValueEqualTo('mode', false)
                .bind(this),
              Validators.required,
            ),
            Validators.required,
          ],
        ],

        simuChoice: [
          this.entity.simuChoice != null ? entity.simuChoice : true,
          [
            this.prFormService.RefreshEveryValidators([
              'amountBorrowed',
              'amountMonthly',
            ]),
            Validators.required,
          ],
        ],
        amountBorrowed: [
          entity ? entity.amountBorrowed : null,
          [
            this.prFormService.AddValidatorsBasedOnCondition(
              this.prFormService
                .isTargetControlValueEqualTo('simuChoice', true)
                .bind(this),
              Validators.required,
            ),
          ],
        ],
        amountMonthly: [
          entity ? entity.amountMonthly : null,
          [
            this.prFormService.AddValidatorsBasedOnCondition(
              this.prFormService
                .isTargetControlValueEqualTo('simuChoice', false)
                .bind(this),
              Validators.required,
            ),
          ],
        ],

        durationTotalInMonth: [
          !isNaN(entity.durationTotalInMonth)
            ? entity.durationTotalInMonth
            : 180,
          Validators.required,
        ],
        amountFee: [entity ? entity.amountFee : null, Validators.required],
        interestRate: [
          !isNaN(entity.interestRate) ? entity.interestRate * 100 : 1.5,
          Validators.required,
        ],

        insuranceType: [
          this.entity.insuranceType != null
            ? this.entity.insuranceType
            : 'AMOUNT_BORROWED',
          [
            this.prFormService.AddValidatorsBasedOnCondition(
              this.prFormService
                .isTargetControlValueEqualTo('mode', false)
                .bind(this),
              Validators.required,
            ),
          ],
        ],
        insuranceRate: [
          !isNaN(entity.insuranceRate)
            ? (entity.insuranceRate * 100).toFixed(2)
            : 0.3,
          Validators.required,
        ],
        insuranceQuotite: this.formBuilder.array(
          entity.insuranceQuotite && entity.insuranceQuotite.length
            ? entity.insuranceQuotite.map(insuranceQuotite =>
                this.formBuilder.group({
                  ownerId: [
                    insuranceQuotite.ownerId != null
                      ? insuranceQuotite.ownerId
                      : null,
                    Validators.required,
                  ],
                  quotite: [
                    insuranceQuotite.quotite != null
                      ? insuranceQuotite.quotite * 100
                      : 100,
                    [
                      Validators.required,
                      Validators.max(100),
                      Validators.min(0),
                    ],
                  ],
                }),
              )
            : [
                this.formBuilder.group({
                  ownerId: [
                    options && options.owners
                      ? this.extractMainOwnerId(options.owners)
                      : null,
                    Validators.required,
                  ],
                  quotite: [100, [Validators.required, Validators.max(100)]],
                }),
              ],
        ),

        differe: [
          this.entity.differe != null ? this.entity.differe : 'TOTAL',
          [
            this.prFormService.AddValidatorsBasedOnCondition(
              isLoanDiffereSectionActive,
              Validators.required,
            ),
          ],
        ],
        durationDiffereInMonth: [
          entity.durationDiffereInMonth != null
            ? entity.durationDiffereInMonth
            : 6,
          [
            this.prFormService.AddValidatorsBasedOnCondition(
              isLoanDiffereSectionActive,
              Validators.required,
            ),
          ],
        ],

        paliersEnabled: [hasLoanPaliers(this.entity)],
        paliers: createLoanPaliersArray(this.formBuilder, this.entity.paliers),

        taeg: [this.entity.taeg != null ? entity.taeg : null],

        startDate: [entity ? entity.startDate : null, Validators.required],
        signatureDate: [
          entity ? entity.signatureDate : null,
          Validators.required,
        ],

        //source: [entity ? entity.source : null],
      },
      options,
    );
    return withLoanFormValidity(form);
  }

  extractMainOwnerId(owners: any[]) {
    let mainOwner = owners.find(({ relation }) => relation === 'MAIN');
    return mainOwner ? mainOwner.id : null;
  }

  /**
   * Valeur initiale du champ `owners` (détenteurs + part de détention).
   * - prêt existant : on reprend `entity.owners` (LoanOwnerDto[]) ;
   * - prêt legacy (anciens `ownerIds`) : répartition égale 1/n ;
   * - nouveau prêt : titulaire principal à 100 %.
   */
  buildLoanInitialOwners(entity: any, options: any): any[] {
    if (entity?.owners?.length) {
      return entity.owners.map(o => ({
        ownerId: o.ownerId,
        share: o.share != null ? o.share : null,
      }));
    }
    if (entity?.ownerIds?.length) {
      const share = Math.round((1 / entity.ownerIds.length) * 100) / 100;
      return entity.ownerIds.map(id => ({ ownerId: id, share }));
    }
    const mainId =
      options && options.owners
        ? this.extractMainOwnerId(options.owners)
        : null;
    return mainId != null ? [{ ownerId: mainId, share: 1 }] : [];
  }

  extract() {
    let dto: any = {};

    dto = {
      id: this.utilsService.emptyIsNull(this.form.value.id),
      delegationAssurance: this.utilsService.emptyIsNull(
        this.form.value.delegationAssurance,
      ),
      accountId: this.utilsService.emptyIsNull(this.form.value.accountId),
      agenceBancaireId: this.utilsService.emptyIsNull(
        this.form.value.agenceBancaireId,
      ),
      providerId: this.utilsService.emptyIsNull(this.form.value.providerId),

      // On n'envoie que `owners` (`{ ownerId, share }`). `ownerIds` est déprécié.
      owners: this.form.value.owners,
      //providerId: this.utilsService.emptyIsNull(this.form.value.providerId),
      label: this.utilsService.emptyIsNull(this.form.value.label),
      category: this.utilsService.emptyIsNull(this.form.value.category),
      //startType: this.utilsService.emptyIsNull(this.form.value.startType),
      insuranceRate: this.utilsService.emptyIsNull(
        this.form.value.insuranceRate / 100,
      ),
      interestRate: this.utilsService.emptyIsNull(
        this.form.value.interestRate / 100,
      ),
      durationTotalInMonth: this.utilsService.emptyIsNull(
        this.form.value.durationTotalInMonth,
      ),
      //source: this.utilsService.emptyIsNull(this.form.value.source),
      startDate: this.utilsService.toIsoDate(
        this.form.value.startDate || new Date(),
      ),
      signatureDate: this.utilsService.toIsoDate(
        this.form.value.signatureDate || new Date(),
      ),
      amountFee: this.emptyIsNull(this.form.value.amountFee),
      insuranceQuotite:
        this.form.value.insuranceQuotite &&
        this.form.value.insuranceQuotite.length
          ? this.form.value.insuranceQuotite.map(insuranceQuotite => ({
              ...insuranceQuotite,
              quotite: insuranceQuotite.quotite
                ? insuranceQuotite.quotite / 100
                : 1,
            }))
          : [
              {
                quotite: 1,
                ownerId: null,
              },
            ],
    };

    if (this.form.value.simuChoice) {
      dto = Object.assign(dto, {
        amountBorrowed: this.emptyIsNull(this.form.value.amountBorrowed),
      });
    } else {
      dto = Object.assign(dto, {
        amountMonthly: this.emptyIsNull(this.form.value.amountMonthly),
      });
    }

    if (this.form.value.mode === false) {
      dto = Object.assign(dto, {
        type: this.emptyIsNull(this.form.value.type),
        differe: this.emptyIsNull(this.form.value.differe),
        durationDiffereInMonth: this.emptyIsNull(
          this.form.value.durationDiffereInMonth,
        ),
      });

      if (this.form.value.type === 'FIX') {
        dto = Object.assign(dto, {
          insuranceType: this.emptyIsNull(this.form.value.insuranceType),
        });
      }
    }
    if (this.form.value.investmentId) {
      dto = Object.assign(dto, {
        investmentIds: [
          this.utilsService.emptyIsNull(this.form.value.investmentId),
        ],
      });
    }

    if (!dto.startType) dto.startType = 'INITIAL';
    if (!dto.startType) dto.source = 'USER';

    return applyPaliersToLoanPayload(dto, this.form.value);
  }
}
